/**
 * Flashcard bulk import CSV parsing and template download.
 *
 * Supported formats:
 * 1. Template: front,back  (front = English prompt, back = Chinese + pinyin)
 * 2. P5–6 export: Question,Correct Answer  (maps Correct Answer → front, Question → back)
 */

export interface ParsedFlashcardRow {
  front: string;
  back: string;
  lineNumber: number;
}

export interface ParseFlashcardCsvResult {
  rows: ParsedFlashcardRow[];
  format: 'front_back' | 'question_correct_answer';
  skipped: number;
  errors: string[];
}

const TEMPLATE_CSV = `front,back
camping,露营 lù yíng
welcome,欢迎 huān yíng
simple,简单 jiǎn dān
`;

/** Download empty template for tutors to fill. */
export function downloadFlashcardImportTemplate(): void {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'flashcards-import-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Minimal RFC4180-style row parse (handles quoted fields). */
function parseCsvRow(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

export function parseFlashcardCsv(text: string): ParseFlashcardCsvResult {
  const errors: string[] = [];
  let skipped = 0;
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  const nonEmpty = lines.map((l, i) => ({ line: l.trim(), lineNumber: i + 1 })).filter((x) => x.line);

  if (nonEmpty.length < 2) {
    return { rows: [], format: 'front_back', skipped: 0, errors: ['File must include a header row and at least one card.'] };
  }

  const headerCells = parseCsvRow(nonEmpty[0].line).map(normalizeHeader);
  const frontIdx = headerCells.indexOf('front');
  const backIdx = headerCells.indexOf('back');
  const questionIdx = headerCells.indexOf('question');
  const answerIdx = headerCells.findIndex(
    (h) => h === 'correct answer' || h === 'correctanswer' || h === 'answer'
  );

  let format: ParseFlashcardCsvResult['format'] = 'front_back';
  let colFront = frontIdx;
  let colBack = backIdx;

  if (questionIdx >= 0 && answerIdx >= 0) {
    format = 'question_correct_answer';
    colFront = answerIdx;
    colBack = questionIdx;
  } else if (frontIdx < 0 || backIdx < 0) {
    return {
      rows: [],
      format: 'front_back',
      skipped: 0,
      errors: [
        'Unrecognized headers. Use front,back OR Question,Correct Answer (see downloadable template).',
      ],
    };
  }

  const rows: ParsedFlashcardRow[] = [];

  for (let i = 1; i < nonEmpty.length; i++) {
    const { line, lineNumber } = nonEmpty[i];
    const cells = parseCsvRow(line);
    const front = (cells[colFront] ?? '').trim();
    const back = (cells[colBack] ?? '').trim();
    if (!front && !back) {
      skipped++;
      continue;
    }
    if (!front || !back) {
      errors.push(`Line ${lineNumber}: both front and back are required.`);
      skipped++;
      continue;
    }
    rows.push({ front, back, lineNumber });
  }

  return { rows, format, skipped, errors };
}
